import { Card, Col, Form, Row } from 'antd'
import {
  ButtonBack,
  ButtonSubmit,
  ButtonAdd,
  ButtonAction
} from 'components/Common/Buttons'
import useTopbarTitleHooks from 'hooks/useTopbarTitleHooks'
import { useAtom } from 'jotai'
import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant'
import useDivisionRepo from 'services/repo/useDivisionRepo'
import useOrgRepo from '../../../services/repo/useOrgRepo'
import { INIT_DIVISION, divisionAtom } from 'store/Division/division'
import { PRIVILEGE_ID } from 'utils/AccessPermissionConfig'
import Constant from 'utils/Constants'
import CustomAlert from 'utils/CustomAlert'
import { setFieldValues } from 'utils/Helper'
import { dismissToast, errorToast, loadingToastId, successToast } from 'utils/Toast'
import {
  CustomFormItemText,
  CustomFormItemSelectDropdown,
  CustomFormItemSwitch
} from 'components/Common/CustomFormItem'
import { CustomAntdTableTemp } from 'components/Common/CustomAntdTableTemp'
import LocalStorageUtils from 'utils/LocalStorage';

export default function ManageDivision() {
  const { divId } = useParams()
  useTopbarTitleHooks(`${divId ? 'Edit' : 'Create'} Division`)
  const { isLoading: isLoadingSave, createDivision, updateDivision } = useDivisionRepo(false)
  const { isLoading: isLoadingGetById, getDivisionById } = useDivisionRepo(false)
  const { isLoading: isLoadingOrgList, getOrgList } = useOrgRepo(false)
  const [dataField, setDataField] = useAtom(divisionAtom)
  const [divisionList, setDivisionList] = useState([])
  const [orgOptions, setOrgOptions] = useState([])
  const userInfo = LocalStorageUtils.userInfo.get();
  const defaultOrgId = userInfo?.orgId || null;
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const isLoadingPrivilege = useRef(true)

  const fetchCheckPrivilege = async () => {
    loadingToastId(Constant.message.loading, PRIVILEGE_ID.AddEditDivision)
    dismissToast(PRIVILEGE_ID.AddEditDivision)
    if (divId) await fetchGetDivisionById(divId)
    isLoadingPrivilege.current = false
  }

  const fetchGetDivisionById = async (id) => {
    const response = await getDivisionById({ divId: id })
    if (response?.detail === 'OK' && response?.status === 200) {
      const data = response.data
      setFieldValues(form, 'divCode', data.divCode)
      setFieldValues(form, 'divName', data.divName)
      setFieldValues(form, 'orgNameOption', data.orgId)
      setFieldValues(form, 'status', String(data.status).toLowerCase() === 'true')

      setDataField({
        divId: data.divId,
        divCode: data.divCode,
        divName: data.divName,
        orgId: data.orgId,
        orgName: data.orgName,
        status: data.status
      })
    }
  }

  const fetchDataOrg = async () => {
    const payload = {
      pageNumber: 1,
      pageSize: 0,
      orderBy: 'orgId',
      isAscending: true,
      filter: {
        orgId: null,
        orgName: '',
        status: 'true'
      }
    }
    const res = await getOrgList(payload)
    if (res?.detail === 'OK' && res?.status === 200) {
      const orgOptions = res.data.content.map((item) => ({
        label: item.orgName,
        value: item.orgId
      }));
      setOrgOptions(orgOptions);

      // ✅ Auto-select jika hanya ada 1 opsi
      if (orgOptions.length === 1) {
        const selectedOrg = orgOptions[0];
        setDataField((prev) => ({ ...prev, orgId: selectedOrg.value }));
        form.setFieldsValue({ orgNameOption: selectedOrg.value });

      }
    }
    // if (res?.detail === 'OK' && res?.status === 200) {
    //   setOrgOptions(
    //     res.data.content.map((item) => ({ label: item.orgName, value: item.orgId }))
    //   )
    // }
  }

  const isDuplicateCode = (code) => {
    return divisionList.some(
      (item) => item.divCode.trim().toLowerCase() === code.trim().toLowerCase()
    )
  }

  const isDifferentOrgInList = (orgId) => {
    return divisionList.some((item) => item.orgId !== orgId)
  }

  const handleAddDivision = useCallback(() => {
    form.validateFields().then(() => {
      // if (isDuplicateCode(dataField.divCode)) {
      //   errorToast(`Division Code "${dataField.divCode}" already exists.`)
      //   return
      // }

      if (!dataField.orgId) {
        errorToast('Organization is required.');
        return;
      }
      const latestStatus = form.getFieldValue("status"); // boolean true/false

      if (isDifferentOrgInList(dataField.orgId)) {
        errorToast('You can only add divisions from the same Organization.')
        return
      }

      setDivisionList((prev) => [
        ...prev,
        {
          id: Date.now(),
          orgId: dataField.orgId,
          orgName: orgOptions.find((o) => o.value === dataField.orgId)?.label || '',
          divCode: dataField.divCode,
          divName: dataField.divName,
          status: latestStatus
        }
      ])
      setDataField({ ...INIT_DIVISION, status: true })
      form.resetFields()
      // setDataField(INIT_DIVISION); // ✅ akan bawa status = 'true'
      form.setFieldsValue({ status: true }); // ✅ sinkron dengan default
    }).catch(() => errorToast(Constant.message.errorForm))
  }, [dataField, divisionList, orgOptions])

  const doSubmit = useCallback(async () => {
    if (!divId) {
      // Create Mode
      if (divisionList.length === 0) {
        errorToast('Division list cannot be empty.');
        return;
      }

      const payloadData = {
        orgId: divisionList[0].orgId,
        divList: divisionList.map((item) => ({
          divCode: item.divCode,
          divName: item.divName,
          status: String(item.status)
        }))
      };

      
      console.log(JSON.stringify(payloadData))
      loadingToastId(Constant.message.savingData, payloadData);
      const response = await createDivision(payloadData);
      dismissToast(payloadData);

      if (response?.detail === 'OK' && response?.status === 200) {
        successToast(Constant.message.saveDataSuccess);
        navigate(APP_ROUTE_PREFIX + '/' + RouteConstant.organization.division.index, { replace: true });
      } else {
        errorToast(Constant.message.fail);
      }

    } else {
      // Edit Mode
      form.validateFields().then(async () => {
        const payload = {
          divId: dataField.divId,
          divCode: dataField.divCode,
          divName: dataField.divName,
          orgId: dataField.orgId,
          status: String(dataField.status),
        };

        loadingToastId(Constant.message.savingData, payload);
        const response = await updateDivision(payload); // Anda perlu pastikan fungsi `updateDivision` ada
        dismissToast(payload);

        if (response?.detail === 'OK' && response?.status === 200) {
          successToast(Constant.message.saveDataSuccess);
          navigate(APP_ROUTE_PREFIX + '/' + RouteConstant.organization.division.index, { replace: true });
        } else {
          errorToast(Constant.message.fail);
        }
      }).catch(() => {
        errorToast(Constant.message.errorForm);
      });
    }
  }, [divisionList, dataField]);

  const handleFormSubmit = (e) => {
    e.preventDefault()
    CustomAlert.confirm({
      title: 'Submit Division!',
      description: 'Are you sure you want to submit this data?',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      type: 'primary',
      onConfirmButton: () => doSubmit()
    })
  }

  const columns = [
    { title: 'Organization', dataIndex: 'orgName' },
    { title: 'Code', dataIndex: 'divCode' },
    { title: 'Name', dataIndex: 'divName' },
    { title: 'Status', dataIndex: 'status', render: (val) => (val ? 'Active' : 'Inactive') },
    {
      title: 'Action',
      key: 'action',
      render: (_, row) => (
        <ButtonAction
          buttons={['delete']}
          onDelete={() =>
            setDivisionList((prev) => prev.filter((item) => item.id !== row.id))
          }
        />
      )
    }
  ]

  useEffect(() => {
    fetchDataOrg()
    fetchCheckPrivilege()
    if (divId) {
      // Jika edit, maka pastikan list kosong
      setDivisionList([]);
    } else {
      setDataField(INIT_DIVISION); // ✅ akan bawa status = 'true'
      form.setFieldsValue({ status: true }); // ✅ sinkron dengan default
    }
    // return () => setDataField(INIT_DIVISION)
  }, [divId])

  return (
    <Card>
      <Form form={form} layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 16 }} labelAlign="left">
        <Row gutter={[16]}>
          <Col xs={24}>
            <CustomFormItemSelectDropdown
              nameFormItem="orgNameOption"
              label="Organization"
              showSearch
              value={dataField.orgId}
              rules={[{ required: true }]}
              placeholder="Select Organization"
              loading={isLoadingOrgList}
              options={orgOptions}
              onChange={(value) => setDataField((prev) => ({ ...prev, orgId: value }))}
            />
          </Col>

          <Col xs={24}>
            <CustomFormItemText
              nameFormItem="divCode"
              label="Code"
              placeholder="Fill in Division Code"
              rules={[{ required: false }]}
              value={dataField.divCode}
              onChange={(e) => setDataField((prev) => ({ ...prev, divCode: e.target.value }))}
            />
          </Col>

          <Col xs={24}>
            <CustomFormItemText
              nameFormItem="divName"
              label="Name"
              placeholder="Fill in Division Name"
              rules={[{ required: true }]}
              value={dataField.divName}
              onChange={(e) => setDataField((prev) => ({ ...prev, divName: e.target.value }))}
            />
          </Col>

          <Col xs={24}>
            <CustomFormItemSwitch
              nameFormItem="status"
              label="Is Active"
              value={dataField.status}
              onChange={(checked) => setDataField((prev) => ({ ...prev, status: checked }))}
            />
          </Col>
          {!divId && (
            <Col span={24} offset={6}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <ButtonAdd buttonText="Add" onClick={handleAddDivision} />
              </div>
            </Col>
          )}
          {!divId && (
            <Col span={24}>
              <div style={{ marginTop: 24 }}>
                <CustomAntdTableTemp
                  columns={columns}
                  dataSource={divisionList}
                  rowKey="id"
                  pagination={false}
                />
              </div>
            </Col>
          )}

          <Col span={24} offset={6}>
            <div style={{ display: 'flex', gap: '10px', margin: '10px 0' }}>
              <ButtonBack onClick={() => navigate(APP_ROUTE_PREFIX + '/' + RouteConstant.organization.division.index)} disabled={isLoadingGetById || isLoadingSave} />
              <ButtonSubmit
                onClick={handleFormSubmit}
                loading={isLoadingSave}
                disabled={
                  (!divId && divisionList.length === 0) || isLoadingGetById || isLoadingSave
                }
              // disabled={divisionList.length === 0 || isLoadingGetById || isLoadingSave}
              />
            </div>
          </Col>
        </Row>
      </Form>
    </Card>
  )
}
